// Expense Manager's own schema, not Message Hub contacts or SMS headers.
// A revision is committed in SyncHistory only AFTER all dataset rows are appended.
export const DATASETS=Object.freeze({
 transactions:{tab:'Transactions',headers:['sync_revision','record_id','date','type','amount','category','payee','status','recurring_rule_id','linked_iou_id','coverage_start','coverage_end','receipt_reference','record_json'],fields:['date','type','amount','category','payee','status','recurringRuleId','linkedIOUId','coveragePeriod.start','coveragePeriod.end','receiptRef']},
 categories:{tab:'Categories',headers:['sync_revision','record_id','name','type','color','record_json'],fields:['name','type','color']},
 categoryRules:{tab:'CategoryRules',headers:['sync_revision','record_id','pattern','match','category','type','enabled','priority','record_json'],fields:['pattern','match','category','type','enabled','priority']},
 budgets:{tab:'Budgets',headers:['sync_revision','record_id','category','amount','period','record_json'],fields:['category','amount','period']},
 people:{tab:'People',headers:['sync_revision','record_id','name','record_json'],fields:['name']},
 recurringRules:{tab:'RecurringRules',headers:['sync_revision','record_id','name','type','amount','frequency','start_date','end_date','daily_breakdown','record_json'],fields:['name','type','amount','frequency','startDate','endDate','generateDailyTransactions']},
 ious:{tab:'IOUs',headers:['sync_revision','record_id','person_id','amount','type','linked_transaction_id','status','record_json'],fields:['personId','amount','type','linkedTransactionId','status']},
 settings:{tab:'Settings',headers:['sync_revision','record_id','value','record_json'],fields:['value']}
});
export const HISTORY='SyncHistory';
export const HISTORY_HEADERS=['sync_revision','saved_at_utc','record_counts_json','sha256','parent_revision','mode','financial_engine_version','changed_counts_json'];
export const APP_INFO='_AppInfo',WELCOME='Start Here',SCHEMA_VERSION='2', FINANCIAL_ENGINE_VERSION='2.0.0';
export const APP_NAME='BPA Expense Manager Pro';
export const APP_IDENTITY='bpa-expense-manager-pro';
// App identity is also checked against VITE_BPA_APP_ID at runtime.
// Categorisation rules live as a settings entry in IndexedDB. Include them
// as a first-class Google worksheet without creating a second local source of truth.
export function prepareSyncSnapshot(backup){
 const data=backup?.data;
 if(!data||!Array.isArray(data.settings))throw new Error('Incomplete local financial backup.');
 for(const key of Object.keys(DATASETS)){
  if(key!=='categoryRules'&&!Array.isArray(data[key]))throw new Error('Missing '+key+' local records.');
 }
 const entries=data.settings.filter(entry=>entry?.key==='categorizationRules');
 if(entries.length>1)throw new Error('Duplicate categorisation-rule settings records.');
 const rules=entries.length?entries[0].value:[];
 if(!Array.isArray(rules))throw new Error('Saved categorisation rules must be an array.');
 const known=new Set();
 for(const rule of rules){
  if(!rule||typeof rule!=='object'||rule.id==null||known.has(String(rule.id)))
   throw new Error('Invalid or duplicate categorisation rule ID.');
  known.add(String(rule.id));
 }
 return {...data,categoryRules:rules};
}
export function validateSyncedRules(data){
 const rules=prepareSyncSnapshot({data}).categoryRules;
 if(JSON.stringify(rules)!==JSON.stringify(data.categoryRules))
  throw new Error('Category Rules worksheet does not match saved automation settings. No local data was changed.');
 return data;
}
export function summariseSyncRecords(data){
 const snapshot=prepareSyncSnapshot({data});
 return Object.fromEntries(Object.keys(DATASETS).map(key=>[key,snapshot[key].length]));
}
export function datasetRow(key,revision,item){
 const spec=DATASETS[key];if(!spec)throw new Error('Unknown dataset');
 const id=key==='settings'?item.key:item.id;
 if(id===undefined||id===null||String(id)==='')throw new Error('Missing record key in '+key);
 return [revision,String(id),...spec.fields.map(k=>{const value=k.split('.').reduce((v,part)=>v?.[part],item);return value==null?'':String(value)}),JSON.stringify(item)];
}
export function decodeRows(key,rows,revision){
 const spec=DATASETS[key];if(!spec)throw new Error('Unknown dataset');
 const result=[];const seen=new Set();
 for(const row of rows||[]){
  if(row[0]!==revision)continue;
  const raw=row[spec.headers.length-1];if(typeof raw!=='string')throw new Error('Incomplete '+key+' snapshot row');
  const item=JSON.parse(raw);const id=key==='settings'?item.key:item.id;
  if(String(id)!==String(row[1])||seen.has(String(id)))throw new Error('Invalid or duplicate '+key+' record');
  seen.add(String(id));result.push(item);
 }
 return result;
}
export function assertUploadAllowed(remoteRevision,baselineRevision){
 if(remoteRevision!==baselineRevision){
  const e=new Error('Google Sheet has changed since your last sync. Download and review its data before uploading. Your local data has not been changed.');e.code='SYNC_CONFLICT';throw e;
 }
}
export function validateSnapshotCounts(snapshot,counts){
 for(const key of Object.keys(DATASETS)){
  if(!Array.isArray(snapshot[key])||snapshot[key].length!==counts[key])throw new Error('Incomplete '+key+' dataset in Google Sheet. Nothing was restored.');
 }
}

// Sync delta records are immutable revision events. An explicit tombstone is
// used for local hard deletes; it never becomes a finance transaction.
export const TOMBSTONE_FIELD='_bpaSyncDeleted';
export const DEVICE_ONLY_SETTINGS = Object.freeze(['backupFolder','lastBackup','autoBackup','autoBackupInterval']);
const unsafeSetting = key => DEVICE_ONLY_SETTINGS.includes(key)||/token|password|secret|api[_-]?key|credential|private[_-]?key|oauth|session|authorization/i.test(String(key));
export function recordKey(dataset,item){
 const raw=dataset==='settings'?item?.key:item?.id;
 if(raw===undefined || raw===null || String(raw)==='')throw new Error('Missing '+dataset+' record identifier');
 return String(raw);
}
export function syncableData(data){
 const clean={};
 for(const key of Object.keys(DATASETS)){
  if(!Array.isArray(data?.[key]))throw new Error('Incomplete '+key+' snapshot');
  clean[key]=key==='settings'?data[key].filter(entry=>!unsafeSetting(entry.key)):data[key];
 }
 return clean;
}
export function stableData(data){
 const filtered=syncableData(data);
 return Object.fromEntries(Object.keys(DATASETS).map(dataset=>[
  dataset,[...filtered[dataset]].sort((a,b)=>recordKey(dataset,a).localeCompare(recordKey(dataset,b)))
 ]));
}
export function recordDelta(before,after){
 const previous=stableData(before),current=stableData(after),changes={};
 for(const dataset of Object.keys(DATASETS)){
  const prior=new Map(previous[dataset].map(row=>[recordKey(dataset,row),row]));
  const now=new Map(current[dataset].map(row=>[recordKey(dataset,row),row]));
  const delta=[];
  for(const [id,value] of now){
   const previousValue=prior.get(id);
   if(!prior.has(id)||JSON.stringify(previousValue)!==JSON.stringify(value))delta.push(value);
  }
  for(const [id] of prior)if(!now.has(id)){
   delta.push(dataset==='settings'?{key:id,[TOMBSTONE_FIELD]:true}:{id,[TOMBSTONE_FIELD]:true});
  }
  changes[dataset]=delta;
 }
 return changes;
}
export function applyRecordDelta(baseline,delta){
 const result=stableData(baseline);
 for(const dataset of Object.keys(DATASETS)){
  const map=new Map(result[dataset].map(row=>[recordKey(dataset,row),row]));
  for(const item of delta[dataset]||[]){
   const id=recordKey(dataset,item);
   if(item[TOMBSTONE_FIELD]===true)map.delete(id);
   else map.set(id,item);
  }
  result[dataset]=[...map.values()];
 }
 return stableData(result);
}
export function validateRecordLinks(data){
 const snapshot=stableData(data);
 for(const dataset of Object.keys(DATASETS)){
  const ids=new Set();
  for(const item of snapshot[dataset]){
   const id=recordKey(dataset,item);
   if(ids.has(id))throw new Error('Duplicate '+dataset+' record ID: '+id);
   ids.add(id);
   if(item[TOMBSTONE_FIELD])throw new Error('Unexpected deletion marker in complete '+dataset+' snapshot');
  }
 }
 // References to intentionally deleted people/rules can exist in older financial
 // transactions. Do not erase history; instead prohibit contradictory active
 // cross-links that would double-count a current IOU settlement.
 const activeTx=new Map(snapshot.transactions.filter(x=>!x.isDeleted).map(x=>[recordKey('transactions',x),x]));
 const linkedOnce=new Set();
 for(const iou of snapshot.ious.filter(i=>!i.isDeleted && i.linkedTransactionId!=null)){
  const linkedId=String(iou.linkedTransactionId);
  if(linkedOnce.has(linkedId))throw new Error('More than one active IOU references transaction '+linkedId);
  linkedOnce.add(linkedId);
  const tx=activeTx.get(linkedId);
  if(tx && tx.linkedIOUId!=null && String(tx.linkedIOUId)!==String(iou.id))
   throw new Error('Conflicting IOU/transaction link for transaction '+iou.linkedTransactionId);
 }
 return snapshot;
}
export function validateRevisionChain(history){
 const seen=new Set();
 let prior='';
 for(const item of history){
  if(!item?.revision || seen.has(item.revision))throw new Error('Duplicate or missing sync revision');
  if(item.parentRevision!==prior)throw new Error('Conflicting sync revision chain; another device may have written concurrently. No data was changed.');
  if(item.engineVersion && item.engineVersion!==FINANCIAL_ENGINE_VERSION)
   throw new Error('Incompatible financial calculation engine. Update the application before restoring.');
  seen.add(item.revision);prior=item.revision;
 }
 return true;
}